"use client"

import { useMemo } from "react"
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { Box, Flex, Text, Image, IconButton, Tooltip, Badge, Select } from "@chakra-ui/react"
import { FaRegUser, FaSignOutAlt, FaMapMarkedAlt, FaTable, FaBars } from "react-icons/fa"

import RetailerNavigation from "../RetailerNavigation"
import OrgToggle from "../OrgToggle"
import useRequestActivity from "../../hooks/useRequestActivity"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"
import { usePageAccess } from "../../wrappers/PageAccessContext"
import { buildLogoCandidates, nextLogoCandidate, BLANK_LOGO } from "../../tools/supplierLogo"
import { neutral, brand, type, radius, layout, elevation, motion } from "../../theme/tokens"
import { supplierLabel } from "../../tools/supplierLabel"

// Titles live with the shell so no page has to render its own — previously
// Sales had no title at all while Strike Rate, Ranging and Margin each styled
// their own differently.
const PAGE_TITLES = {
  "/sales": "Sales",
  "/insights": "Insights",
  "/strike": "Strike Rate",
  "/ranging": "Ranging",
  "/margin": "Margin",
  "/rep": "Rep Sales & Routing",
  "/orders": "Orders",
  "/imports": "Imports",
  "/users": "Users",
  "/articles": "Articles",
  "/stores": "Stores",
  "/rep-stores": "Stores",   // Data → Stores for an organisation with reps (2026-10-16)
  "/rep-list": "Reps",       // Data → Reps (2026-10-16)
  "/loginhistory": "Login History",
  "/edi": "EDI",
  "/alerts": "Stock Alerts",
  "/delete": "Delete Data",
  "/pnp-ftp": "PnP FTP",
  "/validation": "Validation",
  "/data-status": "Data Status",
  "/batch-reports": "Batch Reports",
  "/myProfile": "My Profile",
  "/addUser": "Add User",
  "/supplierLogos": "Supplier Logos",
  "/organisations": "Organisations",
  "/org-reports": "Batch Reports",
  "/editPageAccess": "Page Access",
  "/editSupplierAccess": "Supplier Access",
  "/editRetailerAccess": "Retailer Access",
}

const resolveTitle = (pathname) => {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname]
  const base = `/${pathname.split("/")[1] || ""}`
  return PAGE_TITLES[base] || "SpearBI"
}

/**
 * The command bar.
 *
 * One instrument that answers "what am I looking at?" — page, supplier,
 * retailer channel — and stays pinned while you scroll. It replaces three
 * disconnected pieces of floating chrome: a retailer strip fixed at
 * `left: 200px`, a supplier logo floating over the page in a circle at
 * `z-index: 3000`, and a "Trial Period" label floating beside it.
 */
// onMenu (2026-10-13): set only on a phone — opens the navigation drawer (AuthenticatedLayout).
// supplierPicker (2026-10-13, Helena): a field-only login has no rail, so its supplier switcher is a compact
// dropdown on the right of this bar — "All suppliers" first on a rep's route page (the store page then lists
// every supplier's articles), hidden altogether when there is only one supplier and nothing to switch.
const TopBar = ({ onSignOut, trialyn, showChannels, onMenu = null, supplierPicker = false }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { selectedSupplier, selectedRetailer, retailers, supplierUnpaid, user_supplier_access, setSelectedSupplier, allSuppliersView, setAllSuppliersView } = useSupplierRetailer()
  const onRoutePage = location.pathname.startsWith("/field/route")
  const pickerShown = supplierPicker && (user_supplier_access.length > 1 || onRoutePage)
  const pickerValue = onRoutePage && allSuppliersView ? "" : selectedSupplier || ""
  const pickSupplier = (code) => {
    if (!code) { if (setAllSuppliersView) setAllSuppliersView(true); return }
    setSelectedSupplier(code)
    if (setAllSuppliersView) setAllSuppliersView(false)
  }
  const supplierName = supplierLabel(selectedSupplier, user_supplier_access)
  const { user_page_access } = usePageAccess()
  const busy = useRequestActivity()

  // Dashboard | Map switch — Sales-page-scoped, and only for accounts holding
  // the MapView page grant (docs/michael/TASK-map-view.md §11: no grant, no
  // hint the feature exists). Lives here in the shell so the Sales page itself
  // gives up no content space.
  // The Rep page combines every supplier of the organisation on the Dischem channel, so
  // the identity row says that instead of naming the supplier still selected underneath
  // (2026-09-21). The selection itself is untouched — every other page returns to it.
  const onRep = location.pathname.startsWith("/rep")
  const onSales = location.pathname === "/sales"
  const canMap = onSales && user_page_access.some((p) => p.value === "MapView")
  const mapActive = canMap && searchParams.get("view") === "map"
  const setView = (map) => navigate(map ? "/sales?view=map" : "/sales")

  const title = resolveTitle(location.pathname)
  const logoCandidates = useMemo(() => buildLogoCandidates(selectedSupplier), [selectedSupplier])
  const retailerLabel = retailers.find((r) => r.value === selectedRetailer)?.text || selectedRetailer

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={900}
      bg="rgba(255,255,255,0.86)"
      backdropFilter="blur(12px)"
      boxShadow={elevation.sticky}
    >
      {/* Identity row */}
      <Flex className="sp-topbar-row" align="center" h={layout.topBarHeight} px={layout.contentGutter} gap={3}>
        {onMenu && (
          <IconButton
            aria-label="Open navigation"
            icon={<FaBars />}
            onClick={onMenu}
            variant="ghost"
            size="sm"
            fontSize="18px"
            flexShrink={0}
            ml={-1}
          />
        )}

        {pickerShown && !onRoutePage && (
          <Select
            className="sp-supplier-pick"
            aria-label="Supplier"
            size="sm"
            value={pickerValue}
            onChange={(e) => pickSupplier(e.target.value)}
            w="auto"
            maxW="38vw"
            minW="110px"
            mr={1}
            bg={neutral[0]}
            borderColor={neutral[200]}
            borderRadius={radius.sm}
            fontWeight={type.weight.semibold}
            flexShrink={1}
          >
            {onRoutePage && <option value="">All suppliers</option>}
            {user_supplier_access.map((s) => (
              <option key={s.value} value={s.value}>{supplierLabel(s.value, user_supplier_access)}</option>
            ))}
          </Select>
        )}

        {selectedSupplier && !onRep && !(pickerShown && pickerValue === "") && (
          <Image
            // Remounting per supplier matters: without the key the browser keeps
            // painting the previous logo until the new one decodes.
            key={selectedSupplier}
            src={logoCandidates[0] || BLANK_LOGO}
            onError={({ currentTarget }) => nextLogoCandidate(currentTarget, logoCandidates)}
            alt=""
            boxSize="28px"
            borderRadius={radius.sm}
            objectFit="contain"
            border="1px solid"
            borderColor={neutral[200]}
            bg={neutral[0]}
            flexShrink={0}
          />
        )}

        {/* Field logins (picker shown) have no page title at all — "remove the word SpearBI, then you can use that space"
            (Helena 2026-10-13); the supplier picker takes the room. */}
        <Flex align="baseline" gap={2.5} minW={0} display={pickerShown ? "none" : "flex"}>
          <Text
            as="h1"
            fontSize={type.size.title}
            fontWeight={type.weight.semibold}
            letterSpacing={type.tracking.title}
            color={neutral[900]}
            lineHeight={1.2}
            whiteSpace="nowrap"
          >
            {title}
          </Text>

          {(selectedSupplier || onRep) && !pickerShown && (
            <Text
              fontSize={type.size.small}
              color={neutral[500]}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {onRep ? "Combined · all suppliers" : supplierName}
              {!onRep && retailerLabel ? ` · ${retailerLabel}` : ""}
            </Text>
          )}
        </Flex>


        {trialyn && (
          <Badge bg={brand[50]} color={brand[600]} border="1px solid" borderColor={brand[200]}>
            Trial
          </Badge>
        )}

        {/* The selected supplier's account is suspended. The two master admins
            see the badge as information; every other account also gets the
            report pages replaced by SupplierUnpaidNotice (AuthenticatedLayout). */}
        {supplierUnpaid && !onRep && (
          <Tooltip label={`${supplierName} account suspended`} placement="bottom" openDelay={300}>
            <Badge colorScheme="orange" variant="subtle">
              Suspended
            </Badge>
          </Tooltip>
        )}

        {canMap && (
          <Flex
            ml={2}
            borderRadius={radius.sm}
            border="1px solid"
            borderColor={neutral[200]}
            overflow="hidden"
            flexShrink={0}
            role="group"
            aria-label="Sales view"
          >
            {[
              { map: false, label: "Dashboard", icon: FaTable },
              { map: true, label: "Map", icon: FaMapMarkedAlt },
            ].map(({ map, label, icon: Icon }) => {
              const active = map === mapActive
              return (
                <Flex
                  key={label}
                  as="button"
                  onClick={() => setView(map)}
                  align="center"
                  gap={1.5}
                  px={3}
                  py={1}
                  fontSize={type.size.small}
                  fontWeight={type.weight.semibold}
                  bg={active ? brand[500] : neutral[0]}
                  color={active ? neutral[0] : neutral[600]}
                  _hover={active ? {} : { bg: neutral[100] }}
                  transition={`background ${motion.fast}`}
                  aria-pressed={active}
                >
                  <Icon size={11} />
                  {label}
                </Flex>
              )
            })}
          </Flex>
        )}

        {/* Page controls a page puts in the bar (My route on a phone: Prev / Today / Next, 2026-10-13) — filled with a portal. */}
        <Flex id="sp-topbar-slot" align="center" gap={1} flex="0 1 auto" minW={0} />

        <Box flex="1" />

        <OrgToggle />

        {/* A field login (picker shown) has no profile page — the icon's room goes to the picker and the day buttons. */}
        {!pickerShown && (
          <Tooltip label="My profile" placement="bottom" openDelay={300}>
            <IconButton
              as={NavLink}
              to="/myProfile"
              aria-label="My profile"
              icon={<FaRegUser />}
              variant="ghost"
              size="sm"
              fontSize="14px"
            />
          </Tooltip>
        )}

        <Tooltip label="Sign out" placement="bottom-end" openDelay={300}>
          <IconButton
            aria-label="Sign out"
            icon={<FaSignOutAlt />}
            onClick={onSignOut}
            variant="ghost"
            size="sm"
            fontSize="14px"
            _hover={{ bg: "signal.downSoft", color: "signal.down" }}
          />
        </Tooltip>
      </Flex>

      {/* Loading rail. The sweep animates `transform` only, so it is driven by
          the compositor and keeps moving while the main thread is blocked
          rendering a 20,000-cell grid — which is exactly when you most need to
          know the app is working rather than hung. */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        h="2px"
        overflow="hidden"
        pointerEvents="none"
        opacity={busy ? 1 : 0}
        transition={`opacity ${motion.fast}`}
        aria-hidden={!busy}
      >
        <Box
          h="100%"
          w="40%"
          bg={brand[500]}
          borderRadius="2px"
          sx={{
            willChange: "transform",
            animation: busy ? "spearSweep 1.1s ease-in-out infinite" : "none",
            "@keyframes spearSweep": {
              "0%": { transform: "translateX(-105%)" },
              "100%": { transform: "translateX(355%)" },
            },
            "@media (prefers-reduced-motion: reduce)": { animation: "none", w: "100%", opacity: 0.5 },
          }}
        />
      </Box>

      {/* Channel rail */}
      {showChannels && (
        <Box
          className="sp-channel-rail"
          h={layout.channelRailHeight}
          px={layout.contentGutter}
          borderTop="1px solid"
          borderColor={neutral[200]}
          transition={`opacity ${motion.base}`}
        >
          <RetailerNavigation />
        </Box>
      )}
    </Box>
  )
}

export default TopBar
